import React from "react";
import { Autocomplete, Chip, TextField } from "@mui/material";

/**
 * A free-text tag input, for the CV's short flat lists (compétences, centres d'intérêt).
 *
 * MUI's `Autocomplete` in `multiple freeSolo` mode rather than a hand-rolled
 * "TextField + press Enter + render chips": it already handles the parts that are easy to
 * get wrong — Backspace removing the last chip, chip focus and keyboard removal, and the
 * ARIA wiring that makes the whole thing announce as a list rather than as a text box.
 *
 * `options` is optional and only ever a suggestion list; `freeSolo` means a value that is
 * not in it is still accepted. That matters for skills, where the `postes` referential will
 * never contain every technology someone has worked with, and being pushed toward a wrong
 * nearby option is worse than typing the right one.
 *
 * Purely presentational — `onChange` hands the parent the complete new array, which is how
 * the autosave expects lists (see `sanitizeStringList` on the backend).
 */
export default function ChipsField({
    value = [],
    onChange,
    label,
    placeholder,
    helperText,
    options = [],
    disabled = false,
    /** Matches `MAX_LIST_ITEMS` in the backend's helpers/vresume.js, which truncates past
     *  it. Enforced here too so the cap is visible rather than silently applied on save. */
    maxItems = 40
}) {

    const atLimit = (value || []).length >= maxItems

    return (
        <Autocomplete
            multiple
            freeSolo
            disabled={disabled}
            options={options}
            value={value || []}
            onChange={(event, next) => {
                // Trim, drop blanks, de-duplicate case-insensitively, then cap. A CV listing
                // "React" and "react" as two skills looks like a mistake, because it is one.
                const seen = new Set()
                const cleaned = (next || [])
                    .map(item => String(item || '').trim())
                    .filter(Boolean)
                    .filter(item => {
                        const key = item.toLowerCase()
                        if (seen.has(key)) return false
                        seen.add(key)
                        return true
                    })
                    .slice(0, maxItems)
                onChange(cleaned)
            }}
            renderTags={(tags, getTagProps) =>
                tags.map((tag, index) => (
                    <Chip
                        {...getTagProps({ index })}
                        key={`${tag}-${index}`}
                        label={tag}
                        size="small"
                        sx={{
                            fontFamily: 'roboto',
                            fontSize: 12,
                            backgroundColor: 'rgba(255, 144, 21, 0.12)',
                            color: '#8a4b00',
                            '& .MuiChip-deleteIcon': { color: 'rgba(138, 75, 0, 0.5)' }
                        }}
                    />
                ))
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    size="small"
                    label={label}
                    // The placeholder disappears at the cap so it stops inviting input that
                    // will be dropped.
                    placeholder={atLimit ? '' : placeholder}
                    helperText={helperText}
                    InputProps={{ ...params.InputProps, sx: { fontFamily: 'roboto', fontSize: 13 } }}
                    InputLabelProps={{ sx: { fontFamily: 'roboto', fontSize: 13 } }}
                    FormHelperTextProps={{ sx: { fontFamily: 'roboto', fontSize: 11 } }}
                />
            )}
        />
    )
}
